import React from 'react';

export const ImageDemo = ({ colors }) => {
  const getColor = (index) => colors[index]?.value || colors[0]?.value || '#3b82f6';

  // Convert hex to RGB for overlay effects
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const getOverlayStyle = (color, opacity = 0.6) => {
    const rgb = hexToRgb(color);
    return rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})` : `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Image Effects & Photography</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          See how your color palette transforms real photography with various effects and overlays.
        </p>
      </div>

      {/* Duotone Effects */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h5 className="font-semibold mb-4 text-gray-900 dark:text-white">Duotone Effects</h5>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {colors.slice(0, 3).map((color, index) => {
            const duotoneColor = color.value;
            const shadowColor = colors[(index + 1) % colors.length]?.value || '#000000';
            return (
              <div key={index} className="relative group overflow-hidden rounded-lg">
                <img
                  src={`https://images.unsplash.com/photo-${['1506905925346-21bda4d32df4', '1518837695005-2083093ee35b', '1441974231531-c6227db76b6e'][index]
                    }?auto=format&fit=crop&w=400&h=300`}
                  alt="Duotone Effect"
                  className="w-full h-48 object-cover transition-all duration-300"
                  style={{
                    filter: `sepia(100%) saturate(200%) hue-rotate(${index * 60}deg)`,
                    mixBlendMode: 'multiply'
                  }}
                />
                <div
                  className="absolute inset-0 mix-blend-screen opacity-80"
                  style={{ backgroundColor: duotoneColor }}
                />
                <div
                  className="absolute inset-0 mix-blend-multiply opacity-60"
                  style={{ backgroundColor: shadowColor }}
                />
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                  <p className="text-white text-sm font-medium">Duotone {index + 1}</p>
                  <p className="text-white/80 text-xs">{duotoneColor}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Color Overlay Effects */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h5 className="font-semibold mb-4 text-gray-900 dark:text-white">Color Overlay Styles</h5>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { type: 'Multiply', blend: 'multiply', opacity: 0.7, photo: '1469474968028-56623f02e42e' },
            { type: 'Screen', blend: 'screen', opacity: 0.5, photo: '1506905925346-21bda4d32df4' },
            { type: 'Overlay', blend: 'overlay', opacity: 0.6, photo: '1441974231531-c6227db76b6e' },
            { type: 'Soft Light', blend: 'soft-light', opacity: 0.8, photo: '1518837695005-2083093ee35b' }
          ].map((effect, index) => (
            <div key={index} className="relative group overflow-hidden rounded-lg">
              <img
                src={`https://images.unsplash.com/photo-${effect.photo}?auto=format&fit=crop&w=300&h=200`}
                alt={`${effect.type} Effect`}
                className="w-full h-32 object-cover"
              />
              <div
                className="absolute inset-0 transition-opacity duration-300 group-hover:opacity-90"
                style={{
                  backgroundColor: getOverlayStyle(getColor(index), effect.opacity),
                  mixBlendMode: effect.blend
                }}
              />
              <div className="absolute bottom-2 left-2 text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                {effect.type}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hero Text Overlays */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h5 className="font-semibold mb-4 text-gray-900 dark:text-white">Hero Text Treatments</h5>
        <div className="space-y-4">
          {[
            {
              photo: '1506905925346-21bda4d32df4',
              title: 'Brand Excellence',
              subtitle: 'Elevating experiences through design',
              style: 'gradient'
            },
            {
              photo: '1441974231531-c6227db76b6e',
              title: 'Creative Vision',
              subtitle: 'Where innovation meets inspiration',
              style: 'solid'
            }
          ].map((hero, index) => (
            <div key={index} className="relative overflow-hidden rounded-xl h-64">
              <img
                src={`https://images.unsplash.com/photo-${hero.photo}?auto=format&fit=crop&w=800&h=400`}
                alt="Hero Background"
                className="w-full h-full object-cover"
              />

              {/* Overlay */}
              <div
                className="absolute inset-0"
                style={{
                  background: hero.style === 'gradient'
                    ? `linear-gradient(45deg, ${getOverlayStyle(getColor(0), 0.7)}, ${getOverlayStyle(getColor(1), 0.4)})`
                    : getOverlayStyle(getColor(index), 0.6)
                }}
              />

              {/* Content */}
              <div className="absolute inset-0 flex items-center justify-center text-center text-white">
                <div className="space-y-3">
                  <h2
                    className="text-4xl font-bold"
                    style={{
                      textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                      color: hero.style === 'gradient' ? 'white' : getColor(2)
                    }}
                  >
                    {hero.title}
                  </h2>
                  <p className="text-lg opacity-90 max-w-md">{hero.subtitle}</p>
                  <button
                    className="px-8 py-3 rounded-full font-semibold text-white transition-transform hover:scale-105"
                    style={{ backgroundColor: getColor(2) }}
                  >
                    Explore More
                  </button>
                </div>
              </div>

              {/* Accent Elements */}
              <div className="absolute top-4 right-4 flex gap-2">
                {colors.slice(0, 3).map((color, i) => (
                  <div
                    key={i}
                    className="w-3 h-3 rounded-full opacity-80"
                    style={{ backgroundColor: color.value }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Instagram-Style Filters */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h5 className="font-semibold mb-4 text-gray-900 dark:text-white">Social Media Filters</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { name: 'Warm', photo: '1506905925346-21bda4d32df4', filter: 'sepia(30%) saturate(130%) brightness(110%)' },
            { name: 'Cool', photo: '1441974231531-c6227db76b6e', filter: 'hue-rotate(180deg) saturate(120%) brightness(95%)' },
            { name: 'Vintage', photo: '1518837695005-2083093ee35b', filter: 'sepia(50%) contrast(120%) saturate(80%)' },
            { name: 'Bright', photo: '1469474968028-56623f02e42e', filter: 'brightness(120%) contrast(110%) saturate(130%)' }
          ].map((filter, index) => (
            <div key={index} className="relative group">
              <div className="overflow-hidden rounded-lg">
                <img
                  src={`https://images.unsplash.com/photo-${filter.photo}?auto=format&fit=crop&w=250&h=250`}
                  alt={`${filter.name} Filter`}
                  className="w-full h-32 object-cover transition-transform duration-300 group-hover:scale-110"
                  style={{ filter: filter.filter }}
                />
                <div
                  className="absolute inset-0 opacity-30 mix-blend-overlay"
                  style={{ backgroundColor: getColor(index) }}
                />
              </div>
              <div className="absolute bottom-2 left-2 right-2">
                <span className="text-white text-xs font-medium bg-black/70 px-2 py-1 rounded-full">
                  {filter.name}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Brand Photography Applications */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h5 className="font-semibold mb-4 text-gray-900 dark:text-white">Brand Photography</h5>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Product Photography */}
          <div className="space-y-3">
            <h6 className="font-medium text-gray-900 dark:text-white">Product Showcase</h6>
            <div className="relative overflow-hidden rounded-lg">
              <img
                src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=500&h=300"
                alt="Product"
                className="w-full h-48 object-cover"
              />

              {/* Brand Color Frame */}
              <div className="absolute inset-0 border-8 pointer-events-none"
                style={{ borderColor: getColor(0) }}
              />

              {/* Corner Accent */}
              <div
                className="absolute top-0 right-0 w-16 h-16"
                style={{
                  background: `linear-gradient(-45deg, ${getColor(1)} 50%, transparent 50%)`
                }}
              />

              {/* Brand Badge */}
              <div className="absolute bottom-4 left-4">
                <div
                  className="px-3 py-2 rounded-lg backdrop-blur-sm text-white text-sm font-medium"
                  style={{ backgroundColor: getOverlayStyle(getColor(2), 0.9) }}
                >
                  Premium Collection
                </div>
              </div>
            </div>
          </div>

          {/* Lifestyle Photography */}
          <div className="space-y-3">
            <h6 className="font-medium text-gray-900 dark:text-white">Lifestyle Branding</h6>
            <div className="relative overflow-hidden rounded-lg">
              <img
                src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=500&h=300"
                alt="Lifestyle"
                className="w-full h-48 object-cover"
              />

              {/* Gradient Overlay */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, transparent 40%, ${getOverlayStyle(getColor(0), 0.8)} 100%)`
                }}
              />

              {/* Text Content */}
              <div className="absolute bottom-4 right-4 text-right text-white">
                <h3 className="text-lg font-bold mb-1">Live Boldly</h3>
                <p className="text-sm opacity-90">Embrace every moment</p>
              </div>

              {/* Color Accent Line */}
              <div
                className="absolute left-0 top-1/2 w-1 h-16 transform -translate-y-1/2"
                style={{ backgroundColor: getColor(2) }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Text Over Images Showcase */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h5 className="font-semibold mb-4 text-gray-900 dark:text-white">Typography on Photography</h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative overflow-hidden rounded-lg group">
            <img
              src="https://images.unsplash.com/photo-1518837695005-2083093ee35b?auto=format&fit=crop&w=400&h=300"
              alt="Typography Example"
              className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105"
            />

            {/* Dark overlay for text readability */}
            <div className="absolute inset-0 bg-black/40" />

            {/* Colored accent overlay */}
            <div
              className="absolute inset-0 mix-blend-multiply opacity-40"
              style={{ backgroundColor: getColor(0) }}
            />

            {/* Text content */}
            <div className="absolute inset-0 flex flex-col justify-center items-center text-center text-white p-6">
              <h2
                className="text-2xl font-bold mb-3"
                style={{
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                  color: getColor(1)
                }}
              >
                Innovation
              </h2>
              <p className="text-base opacity-90 mb-4 max-w-xs leading-relaxed">
                Where creativity meets technology in perfect harmony
              </p>
              <div
                className="w-16 h-0.5 mb-4"
                style={{ backgroundColor: getColor(2) }}
              />
              <button
                className="px-6 py-2 border-2 text-white hover:bg-white transition-colors duration-300 rounded-full"
                style={{
                  borderColor: getColor(2),
                  color: getColor(2)
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = getColor(2);
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = getColor(2);
                }}
              >
                Learn More
              </button>
            </div>
          </div>

          {/* Second text example with different treatment */}
          <div className="relative overflow-hidden rounded-lg">
            <img
              src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=400&h=300"
              alt="Typography Example 2"
              className="w-full h-56 object-cover"
            />

            {/* Gradient overlay from brand colors */}
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(45deg, ${getOverlayStyle(getColor(0), 0.8)} 0%, ${getOverlayStyle(getColor(1), 0.6)} 50%, transparent 100%)`
              }}
            />

            {/* Typography */}
            <div className="absolute top-6 left-6 text-white max-w-xs">
              <h3 className="text-3xl font-bold mb-2 leading-tight">
                Future
                <br />
                <span style={{ color: getColor(2) }}>Forward</span>
              </h3>
              <p className="text-sm opacity-90 mb-4">
                Pioneering tomorrow's solutions today
              </p>
              <div className="flex gap-2">
                {colors.slice(0, 3).map((color, i) => (
                  <div
                    key={i}
                    className="w-8 h-1 rounded-full"
                    style={{ backgroundColor: color.value }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Glassmorphism & Blur Effects */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h5 className="font-semibold mb-4 text-gray-900 dark:text-white">Modern Glass Effects</h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="relative overflow-hidden rounded-2xl h-64">
            <img
              src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=500&h=350"
              alt="Glassmorphism"
              className="w-full h-full object-cover"
            />
            {/* Glassmorphic Card */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="p-8 rounded-2xl backdrop-blur-md border"
                style={{
                  backgroundColor: `${getOverlayStyle(getColor(0), 0.15)}`,
                  borderColor: `${getOverlayStyle(getColor(0), 0.3)}`,
                  boxShadow: `0 8px 32px 0 ${getOverlayStyle(getColor(0), 0.2)}`
                }}
              >
                <h3 className="text-2xl font-bold text-white mb-2" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
                  Glassmorphism
                </h3>
                <p className="text-white text-sm">Modern blur effects</p>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl h-64">
            <img
              src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=500&h=350"
              alt="Frosted Glass"
              className="w-full h-full object-cover"
            />
            {/* Frosted bottom bar */}
            <div className="absolute bottom-0 left-0 right-0">
              <div
                className="p-6 backdrop-blur-xl"
                style={{
                  backgroundColor: `${getOverlayStyle(getColor(1), 0.25)}`,
                  borderTop: `1px solid ${getOverlayStyle(getColor(1), 0.4)}`
                }}
              >
                <h4 className="text-white font-bold mb-1">Frosted Interface</h4>
                <p className="text-white text-xs opacity-90">Elegant transparency</p>
                <div className="flex gap-2 mt-3">
                  {colors.slice(0, 4).map((color, i) => (
                    <div key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: color.value }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Split Screen Effects */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h5 className="font-semibold mb-4 text-gray-900 dark:text-white">Split Screen Treatments</h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Vertical Split */}
          <div className="relative overflow-hidden rounded-lg h-48 flex">
            <div className="w-1/2 relative">
              <img
                src="https://images.unsplash.com/photo-1518837695005-2083093ee35b?auto=format&fit=crop&w=300&h=250"
                alt="Split Left"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{ backgroundColor: getOverlayStyle(getColor(0), 0.3) }} />
            </div>
            <div className="w-1/2 relative">
              <img
                src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=300&h=250"
                alt="Split Right"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{ backgroundColor: getOverlayStyle(getColor(1), 0.3) }} />
            </div>
            {/* Center divider */}
            <div className="absolute left-1/2 top-0 bottom-0 w-1 transform -translate-x-1/2" style={{ backgroundColor: getColor(2) }} />
          </div>

          {/* Diagonal Split */}
          <div className="relative overflow-hidden rounded-lg h-48">
            <div className="absolute inset-0" style={{ clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}>
              <img
                src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=400&h=250"
                alt="Diagonal Top"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{ backgroundColor: getOverlayStyle(getColor(0), 0.4) }} />
            </div>
            <div className="absolute inset-0" style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}>
              <img
                src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=400&h=250"
                alt="Diagonal Bottom"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{ backgroundColor: getOverlayStyle(getColor(1), 0.4) }} />
            </div>
          </div>
        </div>
      </div>

      {/* Gradient Masks & Vignettes */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h5 className="font-semibold mb-4 text-gray-900 dark:text-white">Gradient Masks & Vignettes</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: 'Top Fade', gradient: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, transparent 50%)', photo: '1506905925346-21bda4d32df4' },
            { name: 'Radial Vignette', gradient: 'radial-gradient(circle, transparent 30%, rgba(0,0,0,0.7) 100%)', photo: '1441974231531-c6227db76b6e' },
            { name: 'Corner Fade', gradient: 'linear-gradient(135deg, transparent 40%, rgba(0,0,0,0.7) 100%)', photo: '1518837695005-2083093ee35b' },
            { name: 'Edge Darken', gradient: 'linear-gradient(to right, rgba(0,0,0,0.5) 0%, transparent 20%, transparent 80%, rgba(0,0,0,0.5) 100%)', photo: '1469474968028-56623f02e42e' }
          ].map((effect, index) => (
            <div key={index} className="relative overflow-hidden rounded-lg">
              <img
                src={`https://images.unsplash.com/photo-${effect.photo}?auto=format&fit=crop&w=250&h=200`}
                alt={effect.name}
                className="w-full h-32 object-cover"
              />
              <div className="absolute inset-0" style={{ background: effect.gradient }} />
              <div className="absolute inset-0 mix-blend-overlay" style={{ backgroundColor: getOverlayStyle(getColor(index), 0.2) }} />
              <div className="absolute bottom-2 left-2 right-2">
                <span className="bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                  {effect.name}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Picture Frames & Borders */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h5 className="font-semibold mb-4 text-gray-900 dark:text-white">Decorative Frames</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Classic Border */}
          <div className="p-2 rounded-lg" style={{ backgroundColor: getColor(0) }}>
            <img
              src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=250&h=200"
              alt="Classic Frame"
              className="w-full h-32 object-cover rounded"
            />
          </div>

          {/* Double Border */}
          <div className="p-1 rounded-lg" style={{ backgroundColor: getColor(1) }}>
            <div className="p-2 bg-white dark:bg-gray-900 rounded">
              <img
                src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=250&h=200"
                alt="Double Frame"
                className="w-full h-28 object-cover rounded"
              />
            </div>
          </div>

          {/* Gradient Frame */}
          <div className="p-2 rounded-lg" style={{
            background: `linear-gradient(135deg, ${getColor(0)} 0%, ${getColor(2)} 100%)`
          }}>
            <img
              src="https://images.unsplash.com/photo-1518837695005-2083093ee35b?auto=format&fit=crop&w=250&h=200"
              alt="Gradient Frame"
              className="w-full h-32 object-cover rounded"
            />
          </div>

          {/* Shadow Frame */}
          <div className="rounded-lg overflow-hidden" style={{
            boxShadow: `0 10px 40px ${getOverlayStyle(getColor(3), 0.4)}`
          }}>
            <img
              src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=250&h=200"
              alt="Shadow Frame"
              className="w-full h-32 object-cover"
            />
          </div>
        </div>
      </div>

      {/* Collage & Grid Layouts */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h5 className="font-semibold mb-4 text-gray-900 dark:text-white">Grid & Collage Layouts</h5>
        <div className="space-y-4">
          {/* 3-Column Grid */}
          <div className="grid grid-cols-3 gap-2">
            {['1506905925346-21bda4d32df4', '1441974231531-c6227db76b6e', '1518837695005-2083093ee35b'].map((photo, index) => (
              <div key={index} className="relative overflow-hidden rounded-lg group">
                <img
                  src={`https://images.unsplash.com/photo-${photo}?auto=format&fit=crop&w=300&h=200`}
                  alt={`Grid ${index + 1}`}
                  className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div
                  className="absolute inset-0 mix-blend-multiply opacity-30 group-hover:opacity-50 transition-opacity"
                  style={{ backgroundColor: getColor(index) }}
                />
              </div>
            ))}
          </div>

          {/* Magazine Layout */}
          <div className="grid grid-cols-4 gap-2">
            <div className="col-span-2 row-span-2 relative overflow-hidden rounded-lg">
              <img
                src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=500&h=500"
                alt="Featured"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{
                background: `linear-gradient(to top, ${getOverlayStyle(getColor(0), 0.7)} 0%, transparent 50%)`
              }} />
              <div className="absolute bottom-4 left-4 text-white">
                <h3 className="text-xl font-bold mb-1">Featured Story</h3>
                <p className="text-sm opacity-90">Explore the details</p>
              </div>
            </div>

            {['1506905925346-21bda4d32df4', '1441974231531-c6227db76b6e'].map((photo, index) => (
              <div key={index} className="col-span-2 relative overflow-hidden rounded-lg">
                <img
                  src={`https://images.unsplash.com/photo-${photo}?auto=format&fit=crop&w=300&h=150`}
                  alt={`Small ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{ backgroundColor: getOverlayStyle(getColor(index + 1), 0.2) }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tinted Monochrome */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h5 className="font-semibold mb-4 text-gray-900 dark:text-white">Tinted Monochrome Effects</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {colors.slice(0, 4).map((color, index) => (
            <div key={index} className="relative overflow-hidden rounded-lg group">
              <img
                src={`https://images.unsplash.com/photo-${['1506905925346-21bda4d32df4', '1441974231531-c6227db76b6e', '1518837695005-2083093ee35b', '1469474968028-56623f02e42e'][index]}?auto=format&fit=crop&w=250&h=200`}
                alt={`Tinted ${index + 1}`}
                className="w-full h-32 object-cover"
                style={{ filter: 'grayscale(100%)' }}
              />
              <div
                className="absolute inset-0 mix-blend-screen opacity-60 group-hover:opacity-80 transition-opacity"
                style={{ backgroundColor: color.value }}
              />
              <div className="absolute bottom-2 left-2">
                <div className="w-3 h-3 rounded-full border-2 border-white" style={{ backgroundColor: color.value }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Final CTA Section */}
      <div
        className="relative overflow-hidden rounded-xl h-40 flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=800&h=300)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${getOverlayStyle(getColor(0), 0.9)}, ${getOverlayStyle(getColor(1), 0.7)})`
          }}
        />
        <div className="relative z-10 text-center text-white">
          <h3 className="text-2xl font-bold mb-2">Ready to Transform Your Visuals?</h3>
          <p className="text-sm opacity-90 mb-4">Apply these effects to your own photography</p>
          <div className="flex justify-center gap-2">
            {colors.slice(0, 4).map((color, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: color.value }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};